<template>
  <div class="event-component entity-scenes">
    <div class="properties-form">
      <div v-if="eventData.gid !== null">
        <dynamic-display :load-data-api="getLoadEntityApi()" @loadEnd="onLoadEnd"
                         :load-data-on-created="true" ref="dynamicDisplay" class="dynamic-display">
          <template v-slot:showResultsContainer>
            <div v-if="entityDetail">
              <div class="card">
                <div class="card-header">
                  <h1>
                    {{ entityDetail.nameEn }}
                    <br>
                    {{ entityDetail.nameCh }}
                  </h1>
                  <div>
                    <a :href="reviewUrl" target="_blank">Review in AdminHK</a>
                  </div>
                </div>
                <div>
                  <span v-if="entityDetail.placeInc">{{ entityDetail.placeInc }}</span>
                  <span class="text-gray"> (ID: {{ eventData.gid }})</span>
                </div>
                <div class="sections">
                  <div class="section">
                    <div class="section-header with-controls">
                      <div class="header-text">
                        Roles
                      </div>
                    </div>
                    <div class="section-body">

                      <h5 class="mb-2 mt-1">Effective Date <span class="font-weight-normal text-gray">(Required)</span>
                      </h5>
                      <text-field v-bind="propsForDateField"
                                  :initial-value="eventData.effective_date_ts"
                                  @valueChanged="onValueChangedFromInput(inputNames.effective_date_ts, $event)"
                                  ref="effectiveDateInput"
                      ></text-field>

                      <div class="more-dropdown-button-container">
                        <h5 class="mb-2 tool-row">Bankers
                          <create-entity-dropdown class="more-dropdown-button"
                                                  :create-person="false"
                                                  :copy-entities="false"
                                                  :paste-entities="false"
                          ></create-entity-dropdown>
                        </h5>

                        <autocomplete-box entity-type="company"
                                          :initial-value="eventData.banker_gids"
                                          @valueChanged="onValueChangedFromInput(inputNames.bankers, $event)"
                                          ref="bankersInput"
                        >
                        </autocomplete-box>
                      </div>

                      <div class="more-dropdown-button-container">
                        <h5 class="mb-2 tool-row">Legal Advisors
                          <create-entity-dropdown class="more-dropdown-button"
                                                  :create-person="false"
                                                  :copy-entities="false"
                                                  :paste-entities="false"
                          ></create-entity-dropdown>
                        </h5>

                        <autocomplete-box entity-type="company"
                                          :initial-value="eventData.legal_advisor_gids"
                                          @valueChanged="onValueChangedFromInput(inputNames.legal_advisors, $event)"
                                          ref="legalAdvisorsInput"
                        >
                        </autocomplete-box>
                      </div>
                    </div>
                  </div>
                  <div class="section">
                    <div class="section-header with-controls">
                      <div class="header-text">
                        Shareholders
                      </div>
                    </div>
                    <div class="section-body">
                      <h5 class="mb-2 mt-1">Event Date <span class="font-weight-normal text-gray">(Required)</span></h5>
                      <text-field v-bind="propsForDateField"
                                  :initial-value="eventData.share_event_date_ts"
                                  @valueChanged="onValueChangedFromInput(inputNames.share_event_date, $event)"
                                  ref="shareEventDateInput"
                      ></text-field>
                      <h5 class="mb-2">Contents</h5>
                      <multi-obj-box
                          :template="shareholderTemplate"
                          :initial-value="eventData.shareholders"
                          @valueChanged="onValueChangedFromInput(inputNames.shareholders, $event)"
                          ref="shareholdersInput"
                      ></multi-obj-box>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>The company "{{ eventData.gid }}" doesn't exist in AdminHK</div>
          </template>
        </dynamic-display>
      </div>
      <div v-else class="px-4 py-2 text-center text-gray">Missing Document Issuer ID!</div>
    </div>
  </div>
</template>

<script>
import {
  getDataPointInSuggestionItem,
  getReviewCompanyLinkToAdminHK,
  userSettingsManager,
  workModeEdit,
  workModeRead
} from "@/utils";
import {Promise} from 'es6-promise-polyfill';
import {getSuggestedEntities} from "@/apis";
import DynamicDisplay from "@/components/DynamicDisplay";
import AutocompleteBox from "@/InputControls/AutocompleteBox";
import MultiObjBox from "@/InputControls/MultiObjBox";
import TextField from "@/InputControls/TextField";
import {propsForDateField} from "@/InputControls/InputControlTools";
import CreateEntityDropdown from "@/components/CreateEntityDropdown";

const inputNames = {
  bankers: 1,
  legal_advisors: 2,
  shareholders: 3,
  share_event_date: 4,
  effective_date_ts: 5
}

export default {
  name: "DTEventForAR",
  components: {CreateEntityDropdown, TextField, MultiObjBox, AutocompleteBox, DynamicDisplay},
  props: {
    eventObject: Object,
    documentObject: Object,
  },
  data() {
    return {
      initialized: false,
      initializedPromise: null,
      initializedResolvers: [],
      searchEntityManager: {
        showResultsContainer: null,
        suggestionItems: null,
        currentlyLoadingKeywords: null,
        isLoading: null,
        meetLoadingError: null,
        showSuggestedItems: null,
        showEmptyIndicator: null,
      },
      eventData: {
        gid: null,
        banker_gids: [],
        legal_advisor_gids: [],
        shareholders: [],
        share_event_date_ts: null,
        effective_date_ts: null,
      },
      entityDetail: null,
      inputNames: inputNames,
      shareholderTemplate: [
        {
          "field_id": "object1",
          "field_name": "Controlling",
          "is_compulsory": true,
          "data_type": "entity"
        },
        {
          "field_id": "object2",
          "field_name": "Shares",
          "is_compulsory": false,
          "data_type": "integer"
        },
        {
          "field_id": "object3",
          "field_name": "Percent Owned",
          "is_compulsory": false,
          "data_type": "decimal",
          "unit": "percentage"
        }
      ],
      propsForDateField
    }
  },
  mounted() {
    this.initializedPromise = new Promise((resolve) => {
      this.initializedResolvers.push(resolve);
    });
    this.$nextTick(() => {
      this.initializeControls();
    })

    if (this.eventNewlyCreated) {
      const {custom_data: {gekko_id_company}} = this.documentObject;
      if (gekko_id_company !== null && typeof gekko_id_company !== 'undefined') {
        this.setEventData(gekko_id_company);
      }
    }
  },
  computed: {
    eventNewlyCreated() {
      return this.getEventId()[0] === ':';
    },
    workMode() {
      return userSettingsManager.getWorkMode();
    },
    inEditWorkMode() {
      return this.workMode === workModeEdit;
    },
    inReadWorkMode() {
      return this.workMode === workModeRead;
    },
    reviewUrl() {
      return getReviewCompanyLinkToAdminHK(this.eventData.gid);
    }
  },
  watch: {
    workMode(newVal) {
      if (newVal === workModeEdit) {
        this.$nextTick(() => {
          this.initializeControls();
        })
      }
    }
  },
  methods: {
    onLoadEnd(showingResults, results) {
      if (results && results.length) {
        this.entityDetail = this.transformSuggestionItem(results[0]);
      } else {
        this.entityDetail = null;
      }
    },
    initializeControls() {
      if (this.initialized) {
        return;
      }

      const {values} = this.eventObject;
      if (values && 'gid' in values) {
        this.resetForm(values);
      }

      this.initialized = true;
      this.initializedResolvers.forEach(r => {
        r();
      });
    },
    valueChanged() {
      if (!this.initialized) {
        return;
      }

      this.$emit('valueChanged');
    },
    getEventId() {
      return this.eventObject['event_id'];
    },
    getFormValues() {
      if (!this.initialized) {
        throw new Error('Not fully initialized!')
      }
      if (this.eventData.gid !== null
          && this.eventData.share_event_date_ts !== null
          && this.eventData.effective_date_ts !== null) {
        return JSON.parse(JSON.stringify(this.eventData));
      }
      return null;
    },
    openWindowHelper(url) {
      return window.open(url, '', 'width=1200,height=800,menubar=yes,location=no,resizable=yes,scrollbars=yes,status=yes')
    },
    onEntitySearchFieldInput() {
      this.trySearchEntities();
    },
    trySearchEntities() {
      const searchField = this.$refs.searchField;

      const value = searchField.value.trim();

      if (value === this.searchEntityManager.currentlyLoadingKeywords) {
        return;
      }

      if (!value) {
        this.hideSearchEntityResultsContainer();
        return;
      }

      if (this.searchEntityManager.showResultsContainer) {
        this.searchEntityManager.currentlyLoadingKeywords = value;
      } else {
        this.setSearchEntityManagerData(true, null, value, true, false, false, false)
      }

      getSuggestedEntities(value, 20, 'company', true).then(r => {
        if (this.searchEntityManager.currentlyLoadingKeywords !== value) {
          return;
        }
        const data = this.transformSuggestionsData(r.data);
        this.setSearchEntityManagerData(true, data, null, false, false, data.length, !data.length);
      }).catch(err => {
        if (value !== this.searchEntityManager.currentlyLoadingKeywords) {
          return;
        }
        this.setSearchEntityManagerData(true, null, null, false, true, false, false);
        this.handleServerSideError(err).catch(() => {

        })
      })
    },
    setSearchEntityManagerData(showResultsContainer, suggestionItems, currentlyLoadingKeywords, isLoading, meetLoadingError, showSuggestedItems, showEmptyIndicator) {
      this.searchEntityManager.showResultsContainer = showResultsContainer;
      this.searchEntityManager.suggestionItems = suggestionItems;
      this.searchEntityManager.currentlyLoadingKeywords = currentlyLoadingKeywords;
      this.searchEntityManager.isLoading = isLoading;
      this.searchEntityManager.meetLoadingError = meetLoadingError;
      this.searchEntityManager.showSuggestedItems = showSuggestedItems;
      this.searchEntityManager.showEmptyIndicator = showEmptyIndicator;
    },
    hideSearchEntityResultsContainer() {
      this.setSearchEntityManagerData(false, null, null, null, null, null, null);
    },
    transformSuggestionItem(item) {
      item = {
        _source: item,
      }
      let nameEn = getDataPointInSuggestionItem(item, 'Gekko_name_en');
      let nameCh = getDataPointInSuggestionItem(item, 'Gekko_name_ch');
      let placeInc = getDataPointInSuggestionItem(item, 'Place_inc');
      let listedCodes = getDataPointInSuggestionItem(item, 'Listed_code', false);
      let delistedCodes = getDataPointInSuggestionItem(item, 'Delisted_code', false);
      let ownedCodes = getDataPointInSuggestionItem(item, 'owned_codes', false);
      let tradedCodes = getDataPointInSuggestionItem(item, 'traded_codes', false);
      let gid = getDataPointInSuggestionItem(item, 'Gekko_id_company');
      let historicalNames = getDataPointInSuggestionItem(item, 'historical_names', false);
      let compType = getDataPointInSuggestionItem(item, 'Comp_type');

      listedCodes && listedCodes.sort();
      delistedCodes && delistedCodes.sort();
      ownedCodes && ownedCodes.sort();
      tradedCodes && tradedCodes.sort();
      return {
        nameEn,
        nameCh,
        placeInc,
        listedCodes,
        delistedCodes,
        ownedCodes,
        tradedCodes,
        gid,
        historicalNames,
        compType
      }
    },
    transformSuggestionsData(responseData) {
      const data = [];
      for (let item of responseData) {
        data.push(this.transformSuggestionItem(item));
      }
      return data;
    },
    setEventData(gid, banker_gids, legal_advisor_gids, shareholders, share_event_date_ts, effective_date_ts) {
      if (typeof share_event_date_ts === 'undefined') {
        share_event_date_ts = null;
      }
      if (typeof effective_date_ts === 'undefined') {
        effective_date_ts = null;
      }
      if (gid === null) {
        this.eventData = {
          gid: null,
          banker_gids: [],
          legal_advisor_gids: [],
          shareholders: [],
          share_event_date_ts: null,
          effective_date_ts: null
        }
        this.valueChanged();
        return
      }

      gid = parseInt(gid);

      this.eventData = {
        gid: gid,
        banker_gids: banker_gids || [],
        legal_advisor_gids: legal_advisor_gids || [],
        shareholders: shareholders || [],
        share_event_date_ts: share_event_date_ts,
        effective_date_ts: effective_date_ts
      }

      this.valueChanged();
    },
    getLoadEntityApi() {
      return getSuggestedEntities.bind(null, this.eventData.gid, 1, undefined, true);
    },
    resetForm(formData) {
      this.setEventData(formData.gid,
          [].concat(formData.banker_gids || []),
          [].concat(formData.legal_advisor_gids || []),
          [].concat(formData.shareholders || []),
          formData.share_event_date_ts,
          formData.effective_date_ts
      );
      this.repaintUI();
    },
    onValueChangedFromInput(inputName, value) {
      if (inputName === inputNames.bankers) {
        this.eventData.banker_gids = [].concat(value);
      } else if (inputName === inputNames.legal_advisors) {
        this.eventData.legal_advisor_gids = [].concat(value);
      } else if (inputName === inputNames.shareholders) {
        this.eventData.shareholders = [].concat(value);
      } else if (inputName === inputNames.share_event_date) {
        this.eventData.share_event_date_ts = value;
      } else if (inputName === inputNames.effective_date_ts) {
        this.eventData.effective_date_ts = value;
      } else {
        throw new Error(`Invalid input name: ${inputName}`)
      }
      this.valueChanged();
    },
    repaintUI() {
      const {bankersInput, legalAdvisorsInput, shareholdersInput, shareEventDateInput, effectiveDateInput} = this.$refs;
      bankersInput && bankersInput.setInputValue(this.eventData.banker_gids);
      legalAdvisorsInput && legalAdvisorsInput.setInputValue(this.eventData.legal_advisor_gids);
      shareholdersInput && shareholdersInput.setInputValue(this.eventData.shareholders);
      shareEventDateInput && shareEventDateInput.setInputValue(this.eventData.share_event_date_ts);
      effectiveDateInput && effectiveDateInput.setInputValue(this.eventData.effective_date_ts);
    }
  }
}
</script>

<style lang="scss" scoped>
h5 {
  font-weight: 500;
}

.more-dropdown-button-container {
  .tool-row {
    display: flex;
    align-items: center;

    .more-dropdown-button {
      margin-left: auto;
    }
  }
}
</style>